import { useState, useCallback, useEffect, useRef } from 'react';
import { chatAPI } from '../services/api';
import stateManager from '../services/stateManager';

// Module-level so concurrent loadHistory calls — including ones from a
// remounted hook instance (React StrictMode, user/auth deps flipping) — share
// a single in-flight request. The server generates welcome-back messages on
// each call past the 30-min gap, so duplicate requests mean duplicate messages.
let inflightHistoryRequest = null;

export function useChat() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [currentMission, setCurrentMission] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Tappable conversation-suggestion chips (shown when Sophia steers the convo,
  // on the first message, and on welcome-back). Transient — cleared on next send.
  const [suggestions, setSuggestions] = useState([]);
  // NOTE: live streamed text is deliberately NOT surfaced to the UI. It was
  // displayed twice (in the chat, then inside the thinking overlay) and the
  // product owner cut it both times: the answer belongs to the chat when it
  // comes back, with its normal reveal. The streaming transport stays — the
  // endpoint is at feature parity and a future design can re-surface it by
  // passing a real onChunk below.
  // The cycle activity (if any) Sophia just introduced this turn; passed back so
  // the server can un-mark it if the user taps a chip instead of engaging.
  const topicMarkedThisTurnRef = useRef(null);
  // Fallback only — ThinkingIndicator cycles motivational messages and uses
  // this string until its message pool loads (or if no messages are available).
  const loadingMessage = 'Thinking...';
  const loadingHistoryRef = useRef(false);
  // Ids of assistant messages that arrived LIVE from the LLM in this session
  // (send round-trips, plus a greeting the server generated just now). Only
  // these get the word-by-word reveal — text loaded from history has been
  // seen before and should simply be there (product direction).
  const liveMessageIds = useRef(new Set()).current;

  // Inline Celebrate! bubbles: the server delivers a queued fact as a
  // <Celebration:{...}> message DURING the stream (api.js dispatches this
  // event the moment it arrives). Append it right away so it shows while the
  // "(thinking...)" bubble is still up, instead of waiting for the reply.
  // The cache is NOT written here — sendMessage's final assembly (which
  // dedupes by id) persists the full turn once the stream completes.
  useEffect(() => {
    const handleCelebration = (event) => {
      const celebration = event.detail;
      if (!celebration || !celebration.content) return;
      setMessages(prev => {
        if (prev.some(m => m.id === celebration.id)) return prev;
        return [...prev, celebration];
      });
    };
    window.addEventListener('celebrationMessage', handleCelebration);
    return () => window.removeEventListener('celebrationMessage', handleCelebration);
  }, []);

  const loadHistory = useCallback(async () => {
    // Per-instance guard: avoid re-entering while this hook is already loading.
    if (loadingHistoryRef.current) {
      return null;
    }

    loadingHistoryRef.current = true;
    setIsLoading(true);
    try {
      // Check cache first
      const cachedMessages = stateManager.getMessages();
      const cachedConversationId = stateManager.getConversationId();
      const cachedMission = stateManager.getCurrentMission();

      if (cachedMessages && cachedConversationId) {
        setMessages(cachedMessages);
        setConversationId(cachedConversationId);
        setCurrentMission(cachedMission);
        setIsLoading(false);
        loadingHistoryRef.current = false;
        return cachedConversationId;
      }

      // Share a single in-flight fetch across all callers.
      if (!inflightHistoryRequest) {
        inflightHistoryRequest = chatAPI.getHistory().finally(() => {
          inflightHistoryRequest = null;
        });
      }
      const data = await inflightHistoryRequest;

      // A greeting/welcome-back the server generated during THIS call is new
      // text the user has never read — let it animate. Anything older is
      // history. Age is computed against the SERVER's clock (Date response
      // header, surfaced by fetchAPI as _serverDate): message timestamps are
      // server-written, and the client clock has been observed hours off.
      const fetched = data.messages || [];
      const last = fetched[fetched.length - 1];
      const serverNow = data._serverDate ? new Date(data._serverDate).getTime() : Date.now();
      if (
        last &&
        last.role === 'assistant' &&
        // Server marks greetings it generated during THIS call explicitly;
        // the timestamp comparison stays as a fallback for older servers.
        (data.greetingIsLive || (
          last.timestamp &&
          Math.abs(serverNow - new Date(last.timestamp).getTime()) < 60 * 1000
        ))
      ) {
        liveMessageIds.add(last.id);
      }

      stateManager.setMessages(data.messages || []);
      stateManager.setConversationId(data.conversationId);
      stateManager.setCurrentMission(data.currentMission || null);
      setMessages(data.messages || []);
      setConversationId(data.conversationId);
      setCurrentMission(data.currentMission || null);
      setHasMoreMessages(data.hasMoreInBetween || false);
      setSuggestions(data.suggestions || []);
      topicMarkedThisTurnRef.current = data.topicMarkedThisTurn || null;

      return data.conversationId;
    } catch (error) {
      console.error('[useChat] Failed to load history:', error);
      return null;
    } finally {
      setIsLoading(false);
      loadingHistoryRef.current = false;
    }
    // liveMessageIds is a stable Set instance (useRef) — safe dependency.
  }, [liveMessageIds]);

  const sendMessage = async (message, action = null, options = {}) => {
    if (!message.trim()) return;

    console.log('🟢 setIsLoading(TRUE)');
    setIsLoading(true);
    // The user is responding — clear any pending suggestion chips immediately.
    setSuggestions([]);

    // Optimistic echo of the user's message. The old full-screen thinking
    // takeover hid the chat during the wait, so no echo was needed; with the
    // inline "(thinking...)" bubble the conversation stays visible, and a
    // thinking bubble with no question above it reads as broken. The echo is
    // swapped in place for the server-saved message when the stream completes
    // (and removed if the send fails). Not written to the cache until then.
    const pendingUserId = `pending-user-${Date.now()}`;
    if (!options.isHidden) {
      setMessages(prev => [...prev, {
        id: pendingUserId,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      }]);
    }

    try {
      // Streaming endpoint — brought to feature parity with /message on the
      // server's stream-parity branch (suggestions / isHidden / undoTopic /
      // notification tags / mixpanel). First tokens appear as Sophia writes
      // instead of after the full 15-20s round trip.
      const data = await chatAPI.streamMessage(
        message,
        action,
        () => {}, // chunks intentionally not displayed — see note above
        options
      );

      // Update messages with server response
      setMessages(prev => {
        let filtered = [...prev];

        // If we're receiving new plan messages, REMOVE all old plan messages first
        if (data.planMessages && data.planMessages.length > 0) {
          filtered = filtered.filter(m => !m.content.includes('<TodoPlan:'));
        }

        const newMessages = [...filtered];

        // Swap the optimistic echo for the server-saved user message IN PLACE
        // (an inline celebration may already sit after it — pushing to the end
        // would flip their order). Hidden messages provide context to the LLM
        // only and never had an echo, so nothing to do for them.
        if (!options.isHidden && data.userMessage) {
          const pendingIdx = newMessages.findIndex(m => m.id === pendingUserId);
          if (pendingIdx >= 0) {
            newMessages[pendingIdx] = data.userMessage;
          } else {
            newMessages.push(data.userMessage);
          }
        }

        // Inline celebrations were already appended live via the
        // celebrationMessage event — merge any stragglers (dedupe by id).
        if (data.celebrationMessages && data.celebrationMessages.length > 0) {
          data.celebrationMessages.forEach(cm => {
            if (!newMessages.some(m => m.id === cm.id)) {
              newMessages.push(cm);
            }
          });
        }

        // Add assistant message only if it exists (might not exist if Claude only used tools)
        if (data.assistantMessage) {
          newMessages.push(data.assistantMessage);
          liveMessageIds.add(data.assistantMessage.id);
        }

        // Add new plan messages
        if (data.planMessages && data.planMessages.length > 0) {
          newMessages.push(...data.planMessages);
          data.planMessages.forEach(m => liveMessageIds.add(m.id));
        }

        // Add tool responses (final Claude response after tool execution)
        if (data.toolResponses && data.toolResponses.length > 0) {
          newMessages.push(...data.toolResponses);
          data.toolResponses.forEach(m => liveMessageIds.add(m.id));

          // Check if tool responses contain embedded todo widgets
          // If so, refresh todos from server to update singleton cache
          const hasEmbeddedWidgets = data.toolResponses.some(msg =>
            msg.content && msg.content.includes('<TodoWidget:')
          );

          if (hasEmbeddedWidgets && conversationId) {
            console.log('[useChat] Tool response contains embedded widgets - refreshing todos from server');

            // Reload todos in background (don't block UI)
            import('../services/api').then(({ todosAPI }) => {
              todosAPI.getTodos(conversationId)
                .then(freshTodos => {
                  console.log('[useChat] Refreshed todos from server after embedded widget');
                  stateManager.setTodos(freshTodos);
                })
                .catch(err => {
                  console.error('[useChat] Failed to refresh todos:', err);
                });
            });
          }
        }

        // Add new widget messages and refresh todos from server
        if (data.widgetMessages && data.widgetMessages.length > 0) {
          newMessages.push(...data.widgetMessages);
          data.widgetMessages.forEach(m => liveMessageIds.add(m.id));

          // Always refresh todos from server when widgets appear
          // This ensures accurate state display
          if (conversationId) {
            // console.log('[useChat] Widget messages received - refreshing todos from server');

            // Reload todos in background (don't block UI)
            import('../services/api').then(({ todosAPI }) => {
              todosAPI.getTodos(conversationId)
                .then(freshTodos => {
                  // console.log('[useChat] Refreshed todos from server');
                  stateManager.setTodos(freshTodos);
                })
                .catch(err => {
                  console.error('[useChat] Failed to refresh todos:', err);
                });
            });
          }
        }

        // Refresh todos if they were modified via add/edit/remove tools
        if (data.todosUpdated && conversationId) {
          console.log('[useChat] Todos updated via tools - refreshing from server');

          // Reload todos in background (don't block UI)
          import('../services/api').then(({ todosAPI }) => {
            todosAPI.getTodos(conversationId)
              .then(freshTodos => {
                console.log('[useChat] Refreshed todos from server after update');
                stateManager.setTodos(freshTodos);
                // Trigger a re-render by updating state
                // This will cause any components using todos to refresh
                window.dispatchEvent(new CustomEvent('todosRefreshed', { detail: freshTodos }));
              })
              .catch(err => {
                console.error('[useChat] Failed to refresh todos:', err);
              });
          });
        }

        // Update cache
        stateManager.setMessages(newMessages);

        return newMessages;
      });

      setConversationId(data.conversationId);
      stateManager.setConversationId(data.conversationId);

      // Refresh suggestion chips from the response (present when Sophia steered
      // the conversation via get_next_topic; absent on a normal turn → cleared).
      setSuggestions(data.suggestions || []);
      topicMarkedThisTurnRef.current = data.topicMarkedThisTurn || null;
    } catch (error) {
      console.error('[useChat] Failed to send message:', error);
      // Remove the optimistic echo — the message never reached the server.
      setMessages(prev => prev.filter(m => m.id !== pendingUserId));
      throw error;
    } finally {
      console.log('🔴 setIsLoading(FALSE)');
      setIsLoading(false);
      // Surface beads awarded by BACKGROUND work during this turn (the LLM
      // bead-detection classifier runs fire-and-forget server-side and can
      // finish after the stream closed, so no SSE event reaches us). The old
      // ThinkingScreen fired this check on unmount; with the overlay gone,
      // end-of-turn is the equivalent moment. Checked again after a few
      // seconds to catch a classifier that was still running.
      window.dispatchEvent(new CustomEvent('checkBeads'));
      setTimeout(() => window.dispatchEvent(new CustomEvent('checkBeads')), 6000);
    }
  };

  // Send a tapped suggestion chip as a normal (visible) user message, tagged so
  // the backend knows it was a chip and can un-mark the topic Sophia just raised.
  const sendSuggestion = async (suggestion) => {
    if (!suggestion || !suggestion.message) return;
    await sendMessage(suggestion.message, 'suggestion_tapped', {
      suggestionId: suggestion.id,
      undoTopic: topicMarkedThisTurnRef.current
    });
  };

  /**
   * Drop everything we hold about the current conversation WITHOUT touching
   * the server. Used when onboarding ends: approval created a brand new
   * conversation, so the greeting still in state belongs to the throw-away
   * conversation onboarding used, and must not sit in the chat while the real
   * welcome message is being generated.
   */
  const resetChat = useCallback(() => {
    stateManager.invalidate('messages');
    stateManager.invalidate('conversationId');
    stateManager.invalidate('currentMission');
    setMessages([]);
    setConversationId(null);
    setCurrentMission(null);
    setSuggestions([]);
    setHasMoreMessages(false);
  }, []);

  const clearConversation = async () => {
    try {
      await chatAPI.clearConversation();
      setMessages([]);
      setConversationId(null);
      stateManager.clearAll();
    } catch (error) {
      console.error('Failed to clear conversation:', error);
    }
  };

  // Load all messages (for "load more" feature)
  const loadAllMessages = useCallback(async () => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const data = await chatAPI.getAllHistory();
      if (data.messages) {
        setMessages(data.messages);
        stateManager.setMessages(data.messages);
        setHasMoreMessages(false); // All messages loaded
      }
    } catch (error) {
      console.error('[useChat] Failed to load all messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore]);

  return {
    messages,
    isLoading,
    conversationId,
    currentMission,
    loadingMessage,
    hasMoreMessages,
    isLoadingMore,
    suggestions,
    sendMessage,
    sendSuggestion,
    clearConversation,
    resetChat,
    loadHistory,
    loadAllMessages,
    liveMessageIds
  };
}
